import { useState, useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, InputAdornment, Alert, Stack
} from '@mui/material';
import { Add, Edit, Delete, Visibility, VisibilityOff, Download, Upload } from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { downloadBlob, readFileAsText } from './csvUtils';

const ROLES = ['doctor', 'reception', 'nursing', 'diet_coordinator', 'treatment_coordinator', 'admin', 'finance'];
const ROLE_COLORS = {
  doctor: 'secondary', reception: 'info', nursing: 'warning',
  diet_coordinator: 'success', treatment_coordinator: 'primary', admin: 'error', finance: 'success'
};
const EMPTY = { username: '', full_name: '', role: 'reception', email: '', password: '' };

export default function UsersTab({ rows, currentUserId, onRefresh, showSnackbar }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const uploadRef = useRef();

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowPass(false); setOpen(true); };
  const openEdit = (row) => {
    setForm({ username: row.username, full_name: row.full_name, role: row.role, email: row.email || '', password: '' });
    setEditId(row.id);
    setShowPass(false);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.username || !form.full_name || !form.role) {
      showSnackbar('Username, full name and role are required', 'error');
      return;
    }
    if (!editId && !form.password) {
      showSnackbar('Password is required for new users', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await adminAPI.updateUser(editId, form);
        showSnackbar('User updated');
      } else {
        await adminAPI.createUser(form);
        showSnackbar('User created');
      }
      setOpen(false);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteUser(deleteId);
      showSnackbar('User deleted');
      setDeleteId(null);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Delete failed', 'error');
      setDeleteId(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const res = await adminAPI.downloadUsersCSV();
      downloadBlob(res.data, 'users.csv');
    } catch { showSnackbar('CSV download failed', 'error'); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const csv = await readFileAsText(file);
      const res = await adminAPI.uploadUsersCSV(csv);
      const { inserted, updated, errors } = res.data.data;
      showSnackbar(
        `Inserted: ${inserted}, Updated: ${updated}${errors?.length ? `, Errors: ${errors.length}` : ''}`,
        errors?.length ? 'warning' : 'success'
      );
      onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'CSV upload failed', 'error'); }
  };

  const formatRole = (role) => role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Users ({rows.length})</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={handleDownloadCSV}>Download CSV</Button>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => uploadRef.current?.click()}>Upload CSV</Button>
          <input ref={uploadRef} type="file" accept=".csv" hidden onChange={handleUploadCSV} />
          <Button size="small" variant="contained" startIcon={<Add />} onClick={openAdd}>Add User</Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Full Name</strong></TableCell>
              <TableCell><strong>Username</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.full_name}
                  {row.id === currentUserId && (
                    <Chip label="You" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell><code>{row.username}</code></TableCell>
                <TableCell>
                  <Chip
                    label={formatRole(row.role)}
                    size="small"
                    color={ROLE_COLORS[row.role] || 'default'}
                  />
                </TableCell>
                <TableCell>{row.email || '—'}</TableCell>
                <TableCell>{new Date(row.created_at).toLocaleDateString('en-IN')}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error"
                    disabled={row.id === currentUserId}
                    onClick={() => setDeleteId(row.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Full Name *" value={form.full_name}
            onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} fullWidth />
          <TextField label="Username *" value={form.username}
            onChange={e => setForm(f => ({ ...f, username: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Role *</InputLabel>
            <Select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} label="Role *">
              {ROLES.map(r => <MenuItem key={r} value={r}>{formatRole(r)}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Email" type="email" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} fullWidth />
          <TextField
            label={editId ? 'New Password (leave blank to keep current)' : 'Password *'}
            type={showPass ? 'text' : 'password'}
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(s => !s)} edge="end">
                    {showPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {editId && (
            <Alert severity="info" sx={{ py: 0 }}>
              Leave password blank to keep the existing password unchanged.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>This user will lose all access immediately. This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
